import { Antwoord, Controltype, ValidatieParameter } from '@models/claims';
import { IBaseVraagboomResultaat, VraagboomAntwoord, VraagboomDateResultaat, VraagboomDateVanTotResultaat } from '@models/vragenboom';
import { ValidatieregelHelper } from '@services/public_api';

// @dynamic
export class DatumValidator {
  static isValidDate(antwoord: Antwoord, resultaat: VraagboomDateResultaat): boolean {
    if (antwoord == null) {
      return false;
    }

    if (ValidatieregelHelper.contains(antwoord.validatieParameters, 'GELDIGE_DATUM')) {
      if (resultaat) {
        if (resultaat.value == null || isNaN(resultaat.value.getTime())) {
          return false;
        }
      }
    }
    return true;
  }

  static isValidDateVanTot(antwoorden: Antwoord[], resultaat: VraagboomDateVanTotResultaat): boolean {
    if (antwoorden == null || antwoorden.length !== 2) {
      return false;
    }

    if (resultaat) {
      if (ValidatieregelHelper.contains(antwoorden[0].validatieParameters, 'GELDIGE_DATUM') && (resultaat.van == null || isNaN(resultaat.van.getTime()))) {
        return false;
      }

      if (ValidatieregelHelper.contains(antwoorden[1].validatieParameters, 'GELDIGE_DATUM') && (resultaat.tot == null || isNaN(resultaat.tot.getTime()))) {
        return false;
      }
    }
    return true;
  }

  static isSmallerThan(antwoord: Antwoord, resultaat: VraagboomDateResultaat, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): boolean {
    if (antwoord == null) {
      return false;
    }

    if (resultaat) {
      const validatie: ValidatieParameter = ValidatieregelHelper.get(antwoord.validatieParameters, 'DATUM_KLEINER_GELIJK');
      if (validatie)
        return resultaat.value <= this.getComparisonDate(validatie.validatie_parameters, resultaten);
    }
    return true;
  }

  static isGreaterThan(antwoord: Antwoord, resultaat: VraagboomDateResultaat, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): boolean {
    if (antwoord == null) {
      return false;
    }

    if (resultaat) {
      const validatie: ValidatieParameter = ValidatieregelHelper.get(antwoord.validatieParameters, 'DATUM_GROTER_GELIJK');
      if (validatie) {
        return resultaat.value >= this.getComparisonDate(validatie.validatie_parameters, resultaten);
      }
    }
    return true;
  }

  static isVanTotSmallerThan(antwoorden: Antwoord[], resultaat: VraagboomDateVanTotResultaat, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): boolean {
    if (antwoorden == null || antwoorden.length !== 2) {
      return false;
    }

    if (resultaat) {
      let validatie: ValidatieParameter = ValidatieregelHelper.get(antwoorden[0].validatieParameters, 'DATUM_KLEINER_GELIJK');
      if (validatie && resultaat.van > this.getComparisonDate(validatie.validatie_parameters, resultaten)) {
        return false;
      }
      validatie = ValidatieregelHelper.get(antwoorden[1].validatieParameters, 'DATUM_KLEINER_GELIJK');
      if (validatie && resultaat.van > this.getComparisonDate(validatie.validatie_parameters, resultaten)) {
        return false;
      }
    }
    return true;
  }

  static isVanTotGreaterThan(antwoorden: Antwoord[], resultaat: VraagboomDateVanTotResultaat, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): boolean {
    if (antwoorden == null || antwoorden.length !== 2) {
      return false;
    }

    if (resultaat) {
      let validatie: ValidatieParameter = ValidatieregelHelper.get(antwoorden[0].validatieParameters, 'DATUM_GROTER_GELIJK');
      if (validatie && resultaat.van < this.getComparisonDate(validatie.validatie_parameters, resultaten)) {
        return false;
      }
      validatie = ValidatieregelHelper.get(antwoorden[1].validatieParameters, 'DATUM_GROTER_GELIJK');
      if (validatie) {
        let comparisonDate: Date = this.getComparisonDate(validatie.validatie_parameters, resultaten);
        if (comparisonDate == null) {
          comparisonDate = resultaat.van;
        }
        if (resultaat.tot < comparisonDate) {
          return false;
        }
      }
    }
    return true;
  }

  static getComparisonDate(validatie_parameters: string, resultaten: VraagboomAntwoord<IBaseVraagboomResultaat>[]): Date {
    const now: Date = new Date();
    now.setHours(0, 0, 0, 0);

    if (validatie_parameters.toLowerCase() === 'vandaag')
      return now;

    if (validatie_parameters.toLowerCase().startsWith('maanden')) {
      let plus = validatie_parameters.split('+');
      if (plus?.length == 2)
        return new Date(now.setMonth(now.getMonth() + +plus[1]));

      let minus = validatie_parameters.split('-');
      if (minus?.length == 2)
        return new Date(now.setMonth(now.getMonth() - +minus[1]));
    }

    if (validatie_parameters.toLowerCase().startsWith('weken')) {
      let plus = validatie_parameters.split('+');
      if (plus?.length == 2)
        return new Date(now.setDate(now.getDate() + (+plus[1] * 7)));

      let minus = validatie_parameters.split('-');
      if (minus?.length == 2)
        return new Date(now.setDate(now.getDate() - (+minus[1] * 7)));
    }

    // validatie_parameters kan bijv schadedatum onterecht zonder accolades bevatten
    let responseDate = null;
    if (validatie_parameters?.length > 0) {
      if (validatie_parameters.indexOf('{') < 0) {
        validatie_parameters = "{" + validatie_parameters + "}";
      }
      for (const resultaat of resultaten) {
        if (resultaat.vraag?.antwoorden?.length > 0) {
          if (resultaat.vraag.control === Controltype.KalenderVanTot) {
            resultaat.vraag.antwoorden.forEach((antw: Antwoord) => {
              if (antw.variabeleWaarde === validatie_parameters) {
                responseDate = (resultaat as VraagboomAntwoord<VraagboomDateVanTotResultaat>).resultaat.van;
                return;
              }
            });
          } else {
            if (resultaat.vraag.antwoorden[0].variabeleWaarde === validatie_parameters) {
              responseDate = (resultaat as VraagboomAntwoord<VraagboomDateResultaat>).resultaat.value;
            }
          }
        }
      }
    }
    return responseDate;
  }
}
